/**
 * Measurement channel inventory and the remediation price book.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Classified INTERNAL.
 *
 * Prices are maintained ASI estimates, NOT live vendor quotes. Every screen
 * that shows them must say so — the figure is a planning input, not an offer.
 */

import type { MeasurementChannel, PriceBookItem } from '../../contracts';

export const CHANNELS: readonly MeasurementChannel[] = [
  {
    id: 'social',
    name: 'Social Media',
    assets: 184,
    coverage: 92,
    dataPoints: 12480,
    dqs: 88,
    enabled: true,
    vendor: 'Social listening',
    pillar: 'audience',
  },
  {
    id: 'tv',
    name: 'TV Broadcast',
    assets: 42,
    coverage: 86,
    dataPoints: 6240,
    dqs: 91,
    enabled: true,
    vendor: 'TV audience panel',
    pillar: 'audience',
  },
  {
    id: 'radio',
    name: 'Radio / Audio',
    assets: 28,
    coverage: 74,
    dataPoints: 2810,
    dqs: 72,
    enabled: true,
    vendor: 'Audio panel',
    pillar: 'audience',
  },
  {
    id: 'print',
    name: 'Print Press',
    assets: 56,
    coverage: 68,
    dataPoints: 1940,
    dqs: 65,
    enabled: false,
    vendor: 'Press monitoring',
    pillar: 'audience',
  },
  {
    id: 'digitalpr',
    name: 'Digital PR / Online News',
    assets: 312,
    coverage: 95,
    dataPoints: 18800,
    dqs: 84,
    enabled: true,
    vendor: 'Media monitoring',
    pillar: 'audience',
  },
  {
    id: 'ooh',
    name: 'OOH — Static',
    assets: 48,
    coverage: 76,
    dataPoints: 2640,
    dqs: 74,
    enabled: true,
    vendor: 'OOH audit',
    pillar: 'brand',
  },
  {
    id: 'dooh',
    name: 'OOH — Digital (DOOH)',
    assets: 26,
    coverage: 82,
    dataPoints: 1880,
    dqs: 79,
    enabled: true,
    vendor: 'OOH audit',
    pillar: 'brand',
  },
  {
    id: 'streaming',
    name: 'Streaming / OTT',
    assets: 36,
    coverage: 79,
    dataPoints: 3650,
    dqs: 81,
    enabled: true,
    vendor: 'OTT measurement',
    pillar: 'audience',
  },
  {
    id: 'gaming',
    name: 'Gaming / eSports',
    assets: 18,
    coverage: 54,
    dataPoints: 980,
    dqs: 58,
    enabled: false,
    vendor: 'Gaming analytics',
    pillar: 'audience',
  },
  {
    id: 'walking',
    name: 'Walking Billboards',
    assets: 64,
    coverage: 71,
    dataPoints: 2210,
    dqs: 69,
    enabled: true,
    vendor: 'Image recognition',
    pillar: 'brand',
  },
  {
    id: 'vehicle',
    name: 'Vehicle Wraps / Team Bus',
    assets: 22,
    coverage: 88,
    dataPoints: 1340,
    dqs: 82,
    enabled: true,
    vendor: 'Image recognition',
    pillar: 'brand',
  },
  {
    id: 'led',
    name: 'Stadium LED Perimeter',
    assets: 12,
    coverage: 99,
    dataPoints: 5210,
    dqs: 95,
    enabled: true,
    vendor: 'Venue feed',
    pillar: 'brand',
  },
  {
    id: 'static',
    name: 'Stadium Static Signage',
    assets: 38,
    coverage: 91,
    dataPoints: 2840,
    dqs: 86,
    enabled: true,
    vendor: 'Venue feed',
    pillar: 'brand',
  },
  {
    id: 'presser',
    name: 'Press Conf. Backdrops',
    assets: 18,
    coverage: 84,
    dataPoints: 1620,
    dqs: 80,
    enabled: true,
    vendor: 'Media monitoring',
    pillar: 'brand',
  },
  {
    id: 'podcast',
    name: 'Podcasts',
    assets: 14,
    coverage: 47,
    dataPoints: 540,
    dqs: 54,
    enabled: false,
    vendor: 'Audio panel',
    pillar: 'audience',
  },
  {
    id: 'influencer',
    name: 'Influencer / Creator',
    assets: 86,
    coverage: 73,
    dataPoints: 3120,
    dqs: 70,
    enabled: true,
    vendor: 'Creator analytics',
    pillar: 'audience',
  },
  {
    id: 'crm',
    name: 'Email / CRM',
    assets: 24,
    coverage: 92,
    dataPoints: 1840,
    dqs: 87,
    enabled: false,
    vendor: 'Internal CRM',
    pillar: 'commerce',
  },
  {
    id: 'branded',
    name: 'Branded Content / Editorial',
    assets: 32,
    coverage: 77,
    dataPoints: 1410,
    dqs: 73,
    enabled: false,
    vendor: 'Media monitoring',
    pillar: 'brand',
  },
];

export const PRICE_BOOK: readonly PriceBookItem[] = [
  {
    id: 'tv-panel',
    item: 'TV audience panel licence (per market / season)',
    unit: 18000,
    lift: 14,
    applies: ['tv', 'streaming'],
  },
  {
    id: 'social-backfill',
    item: 'Social listening backfill (24 months)',
    unit: 12000,
    lift: 11,
    applies: ['social', 'influencer'],
  },
  {
    id: 'press-monitor',
    item: 'Press & online monitoring upgrade',
    unit: 9500,
    lift: 12,
    applies: ['print', 'digitalpr', 'branded', 'presser'],
  },
  {
    id: 'ooh-audit',
    item: 'OOH audit + reach modelling',
    unit: 14000,
    lift: 13,
    applies: ['ooh', 'dooh', 'walking', 'vehicle'],
  },
  {
    id: 'venue-feed',
    item: 'Venue LED / signage feed integration',
    unit: 7500,
    lift: 8,
    applies: ['led', 'static'],
  },
  {
    id: 'audio-panel',
    item: 'Audio / podcast panel subscription',
    unit: 6500,
    lift: 15,
    applies: ['radio', 'podcast'],
  },
  {
    id: 'gaming-analytics',
    item: 'Gaming / eSports analytics package',
    unit: 11000,
    lift: 16,
    applies: ['gaming'],
  },
  {
    id: 'crm-pipe',
    item: 'CRM export pipeline + consent mapping',
    unit: 5000,
    lift: 9,
    applies: ['crm'],
  },
  {
    id: 'image-rec',
    item: 'Image-recognition asset detection (per season)',
    unit: 16000,
    lift: 12,
    applies: ['walking', 'vehicle', 'static'],
  },
];
